import { Cable } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/_shared/components/ui/tabs';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import slackIconUrl from '../icons/slack.svg';
import { McpIntegrationSection } from './SettingsMcp';
import { SlackIntegrationSection } from './SettingsSlack';

const SECTIONS = ['mcp', 'slack'] as const;
type Section = (typeof SECTIONS)[number];

function isSection(value: string | undefined): value is Section {
  return value !== undefined && (SECTIONS as readonly string[]).includes(value);
}

export function SettingsIntegrations(): React.ReactElement {
  usePageTitle('Integrations — Settings');
  const { wp } = useWorkspace();
  const navigate = useNavigate();
  const { section } = useParams<{ section: string }>();

  // The bare `/settings/integrations` (the tab's own link) opens the default section. An unknown section
  // is an invalid settings path, so it opens General, like every other invalid settings path.
  if (section === undefined) {
    return <Navigate to={wp('/settings/integrations/mcp')} replace />;
  }
  if (!isSection(section)) {
    return <Navigate to={wp('/settings')} replace />;
  }

  return (
    <Tabs value={section} onValueChange={(value) => navigate(wp(`/settings/integrations/${value}`))}>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-base font-bold tracking-wider text-muted-foreground uppercase">Integrations</h2>
        <TabsList className="shrink-0">
          <TabsTrigger value="mcp" className="gap-1.5">
            <Cable className="size-4" />
            MCP
          </TabsTrigger>
          <TabsTrigger value="slack" className="gap-1.5">
            <img src={slackIconUrl} alt="" aria-hidden className="size-4" />
            Slack
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="mcp">
        <McpIntegrationSection />
      </TabsContent>
      <TabsContent value="slack">
        <SlackIntegrationSection />
      </TabsContent>
    </Tabs>
  );
}
